/**
 * Sidebar-footer user menu. Shows the signed-in user's avatar + name and a
 * dropdown containing sign-out.
 *
 * Per `aidlc-docs/construction/auth-workspaces-rbac/nfr-design/frontend-architecture.md § 4.6`.
 *
 * Initials come from the user's email local part as a placeholder; once
 * first/last names are populated by the WorkOS mirror they'll show there
 * via the AvatarFallback children.
 */

import { useAuth } from "@cometchat-ai/auth/client";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
  useTheme,
  type ThemeMode,
} from "@cometchat-ai/ui-dashboard";
import { ChevronsUpDown, LogOut, SunMoon } from "./icons";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { dicebearMicah } from "../lib/avatar";

function initialsFor(email: string | null | undefined): string {
  if (!email) return "?";
  const local = email.split("@")[0] ?? email;
  const parts = local.split(/[._-]/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
  }
  return local.slice(0, 2).toUpperCase();
}

export function UserMenu() {
  const { user, signOut } = useAuth();
  const { t } = useTranslation("common");
  const { isMobile } = useSidebar();
  const { mode, setMode } = useTheme();
  const navigate = useNavigate();

  if (!user) return null;

  const handleSignOut = async () => {
    await signOut();
    navigate("/login");
  };

  const displayName = user.email?.split("@")[0] ?? user.email ?? "";

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              className="h-9 hover:!bg-[color:color-mix(in_oklch,var(--color-fg)_4%,transparent)] data-[state=open]:!bg-[color:color-mix(in_oklch,var(--color-fg)_4%,transparent)] group-data-[collapsible=icon]:!p-0 group-data-[collapsible=icon]:justify-center"
              data-testid="topbar-user-menu"
              aria-label={user.email}
            >
              <Avatar className="h-6 w-6 rounded-md">
                <AvatarImage
                  src={user.profilePictureUrl ?? dicebearMicah(displayName)}
                  alt=""
                  className="rounded-md"
                />
                <AvatarFallback className="rounded-md text-2xs">
                  {initialsFor(user.email)}
                </AvatarFallback>
              </Avatar>
              <span className="min-w-0 flex-1 truncate text-left text-[13px] font-medium group-data-[collapsible=icon]:hidden">
                {displayName}
              </span>
              <ChevronsUpDown className="ml-auto h-3.5 w-3.5 shrink-0 text-[color:var(--color-fg-subtle)] group-data-[collapsible=icon]:hidden" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left">
                <Avatar className="h-8 w-8 rounded-md">
                  <AvatarImage
                    src={user.profilePictureUrl ?? dicebearMicah(displayName)}
                    alt=""
                    className="rounded-md"
                  />
                  <AvatarFallback
                    className="rounded-md text-xs font-medium text-[var(--color-accent)]"
                    style={{
                      background: "color-mix(in oklch, var(--color-accent) 12%, transparent)",
                    }}
                  >
                    {initialsFor(user.email)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex min-w-0 flex-1 flex-col leading-tight">
                  <span className="truncate text-[13px] font-medium">{displayName}</span>
                  <span className="truncate text-xs text-muted-foreground">{user.email}</span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuSub>
              <DropdownMenuSubTrigger data-testid="theme-menu">
                <SunMoon className="mr-2 h-4 w-4" />
                {t("menu.theme")}
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                <DropdownMenuRadioGroup
                  value={mode}
                  onValueChange={(value) => setMode(value as ThemeMode)}
                >
                  <DropdownMenuRadioItem value="light">
                    {t("menu.themeLight")}
                  </DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="dark">{t("menu.themeDark")}</DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="system">
                    {t("menu.themeSystem")}
                  </DropdownMenuRadioItem>
                </DropdownMenuRadioGroup>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleSignOut} data-testid="signout-button">
              <LogOut className="mr-2 h-4 w-4" />
              {t("menu.signOut")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
